import React, { useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { DataGrid } from "@mui/x-data-grid";
import { getAdminAllActivity } from "../../../../../utils/services/control/adminDashboardService";
import { getEventBadge } from "./eventBadge";
import { formatDateTime } from "../../../../../utils/formatters/date";
import ExportExcelButton from "../../../../../components/Basic/ExportExcelButton";

const buildColumns = () => [
  {
    field: "eventDate",
    headerName: "When",
    width: 170,
    renderCell: ({ value }) => formatDateTime(value),
  },
  {
    field: "eventKey",
    headerName: "Action",
    width: 180,
    renderCell: ({ row }) => {
      const badge = getEventBadge(row.eventKey, row.category);
      return (
        <Chip
          size="small"
          label={badge.label}
          color={badge.color}
          variant="outlined"
        />
      );
    },
  },
  { field: "category", headerName: "Category", width: 130 },
  {
    field: "fullName",
    headerName: "Actor",
    flex: 1,
    minWidth: 180,
    renderCell: ({ row }) => (
      <Box sx={{ py: 0.5 }}>
        <Typography variant="body2" fontWeight={600}>
          {row.fullName || "system"}
        </Typography>
        {row.userMail && (
          <Typography variant="caption" color="text.secondary">
            {row.userMail}
          </Typography>
        )}
      </Box>
    ),
  },
  { field: "detail", headerName: "Detail", flex: 1.5, minWidth: 220 },
];

const exportColumns = [
  {
    field: "eventDate",
    headerName: "Event Date",
    valueGetter: (r) => formatDateTime(r.eventDate),
  },
  { field: "eventKey", headerName: "Event Key" },
  { field: "category", headerName: "Category" },
  { field: "employeeId", headerName: "Employee ID" },
  { field: "fullName", headerName: "Full Name" },
  { field: "userMail", headerName: "User Email" },
  { field: "detail", headerName: "Detail" },
];

const RecentActivityWidget = ({ rows, loading }) => {
  const [allOpen, setAllOpen] = useState(false);
  const [allRows, setAllRows] = useState([]);
  const [allLoading, setAllLoading] = useState(false);

  const openAll = async () => {
    setAllOpen(true);
    setAllLoading(true);
    try {
      const data = await getAdminAllActivity();
      setAllRows(Array.isArray(data) ? data : []);
    } finally {
      setAllLoading(false);
    }
  };

  const columns = buildColumns();

  return (
    <>
      <Paper sx={{ p: 2 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 1.5,
          }}
        >
          <Box>
            <Typography variant="subtitle1" fontWeight={700}>
              Recent Activity
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Last 30 days · top {rows?.length || 0} events
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 1 }}>
            <ExportExcelButton
              rows={rows || []}
              columns={exportColumns}
              filename="recent_activity"
            />
            <Button
              size="small"
              variant="text"
              onClick={openAll}
              sx={{ textTransform: "none" }}
            >
              View All
            </Button>
          </Box>
        </Box>
        <Box sx={{ height: 360 }}>
          <DataGrid
            rows={(rows || []).map((r, idx) => ({ id: idx, ...r }))}
            columns={columns}
            loading={loading}
            density="compact"
            hideFooterSelectedRowCount
            pageSizeOptions={[10, 25]}
            initialState={{
              pagination: { paginationModel: { pageSize: 10 } },
            }}
            sx={{ border: "none" }}
          />
        </Box>
      </Paper>

      <Dialog
        open={allOpen}
        onClose={() => setAllOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          All Activity (last 30 days)
          <IconButton size="small" onClick={() => setAllOpen(false)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
            <ExportExcelButton
              rows={allRows}
              columns={exportColumns}
              filename="all_activity"
            />
          </Box>
          <Box sx={{ height: 520 }}>
            <DataGrid
              rows={allRows.map((r, idx) => ({ id: idx, ...r }))}
              columns={columns}
              loading={allLoading}
              density="compact"
              hideFooterSelectedRowCount
              pageSizeOptions={[25, 50, 100]}
              initialState={{
                pagination: { paginationModel: { pageSize: 25 } },
              }}
              sx={{ border: "none" }}
            />
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default RecentActivityWidget;
